// 主人公の致命の光 = 裂け目・ひびの奥から常夜燈の光が漏れる (2026-10-20 作者の答え・配布版)。
//   止め = お手本 a「割れたガラス」: 「ガラスっぽくていいんだけど、常夜との光が漏れているような感じにして欲しいんだよね。」
//   普段の致命 = お手本 b「ひび」: 「ひびはひびでいいんだけど、常夜灯の光がもれているような感じにしてほしいです。」
//   記録 = docs/research/2026-10-20-luna-exec-weight.md「結果」。お手本の絵 = そざい/concept/luna-exec-1020/ (退避先 LUNAIRE-notes)。
//   色 = 常夜燈そのもの (render.ts drawGhostLight の cool と同じ 3 色・同じ揺らぎ)。青白 (SOUL_BLUE) より青く、いちばん青いのは常夜燈の光。
//   線にしない (作者は「線・点線に見える」を2回嫌った): ひび・裂け目は太さの揺れる面で塗り、外へ漏れる光は丸いにじみを重ねる。
//   散る物は棒でなく、面の割れたガラスの欠片 (三角・四角)。
//   どれも時刻だけで決まる (乱数は種つき) = 同じ時刻なら同じ絵。

import { drawLampMote, lampMoteOld } from './lamp_mote'

type Pt = { x: number; y: number }
type Rgb = readonly [number, number, number]

/** 常夜燈の光 (render.ts drawGhostLight の cool: 芯 205,228,255 → 170,205,255 → 外 96,142,228)。 */
export const LAMP = {
  core: [214, 234, 255] as Rgb,
  mid: [170, 205, 255] as Rgb,
  deep: [96, 142, 228] as Rgb,
  /** ガラスの欠片の暗い面 (光を通さない側)。 */
  glassDark: [40, 58, 104] as Rgb,
} as const

function rnd(i: number, seed: number): number {
  const s = Math.sin(i * 127.1 + seed * 311.7) * 43758.5453
  return s - Math.floor(s)
}
const clamp01 = (u: number): number => Math.max(0, Math.min(1, u))
const rgba = (c: Rgb, a: number): string => `rgba(${c[0]},${c[1]},${c[2]},${clamp01(a).toFixed(3)})`

/** 常夜燈と同じ揺らぎ (render.ts drawGhostLight の flick を時刻 s で)。0.85〜1。 */
export function lampFlicker(sec: number, seed = 0): number {
  const t = sec + seed * 0.37
  return 0.85 + 0.15 * Math.sin(t * 9 + Math.sin(t * 23) * 2)
}

function glow(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, c: Rgb, a: number): void {
  if (a <= 0.01 || r <= 0.5) return
  const g = ctx.createRadialGradient(x, y, 0, x, y, r)
  g.addColorStop(0, rgba(c, a)); g.addColorStop(0.4, rgba(c, a * 0.4)); g.addColorStop(1, rgba(c, 0))
  ctx.fillStyle = g
  ctx.beginPath(); ctx.arc(x, y, r, 0, Math.PI * 2); ctx.fill()
}
function fillPoly(ctx: CanvasRenderingContext2D, pts: Pt[], style: string): void {
  if (pts.length < 3) return
  ctx.fillStyle = style
  ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y)
  for (let k = 1; k < pts.length; k++) ctx.lineTo(pts[k].x, pts[k].y)
  ctx.closePath(); ctx.fill()
}

// ---- ひび・裂け目の形 ----

/** 折れ線 (ガラスのひびのように角ばって折れる)。 */
export interface Fissure { pts: Pt[]; /** 各点の半分の太さ (px)。 */ w: number[] }

/** 折れ線を、先頭から reveal (0〜1) の長さまでで切る。 */
function cutFissure(f: Fissure, reveal: number): Fissure {
  if (reveal >= 1) return f
  const seg: number[] = []
  let total = 0
  for (let i = 1; i < f.pts.length; i++) { const d = Math.hypot(f.pts[i].x - f.pts[i - 1].x, f.pts[i].y - f.pts[i - 1].y); seg.push(d); total += d }
  let want = total * clamp01(reveal)
  const pts: Pt[] = [f.pts[0]], w: number[] = [f.w[0]]
  for (let i = 1; i < f.pts.length; i++) {
    const d = seg[i - 1]
    if (want >= d) { pts.push(f.pts[i]); w.push(f.w[i]); want -= d; continue }
    const u = d > 0 ? want / d : 0
    pts.push({ x: f.pts[i - 1].x + (f.pts[i].x - f.pts[i - 1].x) * u, y: f.pts[i - 1].y + (f.pts[i].y - f.pts[i - 1].y) * u })
    w.push(0)   // 開いていく頭は尖る
    break
  }
  return { pts, w }
}

/** 太さを持った面 (折れ線の左右へ w だけ広げる)。 */
function fissurePoly(f: Fissure, mul: number): Pt[] {
  const n = f.pts.length
  if (n < 2) return []
  const left: Pt[] = [], right: Pt[] = []
  for (let i = 0; i < n; i++) {
    const a = f.pts[Math.max(0, i - 1)], b = f.pts[Math.min(n - 1, i + 1)]
    const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy) || 1
    const nx = -dy / len, ny = dx / len
    const w = f.w[i] * mul
    left.push({ x: f.pts[i].x + nx * w, y: f.pts[i].y + ny * w })
    right.push({ x: f.pts[i].x - nx * w * 0.8, y: f.pts[i].y - ny * w * 0.8 })
  }
  return left.concat(right.reverse())
}

/**
 * 1本のひび・裂け目を描く。漏れる光 (外へにじむ丸い光) → 開いた口 (常夜燈の青) → 奥の芯 (白に近い青)。
 *   light = 光の強さ 0〜1 (揺らぎ込み)。
 */
function drawLeak(ctx: CanvasRenderingContext2D, f: Fissure, light: number, bloomR: number, seed: number): void {
  if (light <= 0.01 || f.pts.length < 2) return
  // 漏れる光: 折れ目とその間に丸いにじみを重ね、ひびの形の「光の雲」にする (細い線に見せない)
  for (let i = 0; i < f.pts.length; i++) {
    const w = f.w[i]
    if (w <= 0.2) continue
    const r = bloomR * (0.8 + rnd(i, seed + 3) * 0.7) * (0.6 + 0.4 * Math.min(1, w / 3))
    glow(ctx, f.pts[i].x, f.pts[i].y, r, LAMP.deep, 0.55 * light)
    glow(ctx, f.pts[i].x, f.pts[i].y, r * 0.5, LAMP.mid, 0.3 * light)
    const nx = f.pts[i + 1]
    if (nx && f.w[i + 1] > 0.2) glow(ctx, (f.pts[i].x + nx.x) / 2, (f.pts[i].y + nx.y) / 2, r * 0.8, LAMP.deep, 0.4 * light)
  }
  // 口: 縁をぼかして塗る (くっきりした筆の線にしない)。白に近いのは奥の細い所だけ・薄く
  ctx.save()
  ctx.shadowColor = rgba(LAMP.deep, 0.6 * light)
  ctx.shadowBlur = bloomR * 0.7
  fillPoly(ctx, fissurePoly(f, 1), rgba(LAMP.deep, 0.6 * light))
  ctx.restore()
  fillPoly(ctx, fissurePoly(f, 0.55), rgba(LAMP.mid, 0.45 * light))
  fillPoly(ctx, fissurePoly(f, 0.2), rgba(LAMP.core, 0.35 * light))
}

/** 起点から外へ伸びるひびの枝 (角ばって折れる)。dirAng = 伸びる向き。 */
function branch(origin: Pt, dirAng: number, length: number, rootW: number, seed: number, steps: number): Fissure {
  const pts: Pt[] = [origin], w: number[] = [rootW]
  let x = origin.x, y = origin.y, ang = dirAng
  for (let s = 1; s <= steps; s++) {
    // ガラスの割れ目のように、まっすぐ伸びて折れる (曲線にしない)
    ang += (rnd(s, seed) - 0.5) * 0.9
    const d = length / steps * (0.7 + rnd(s, seed + 1) * 0.6)
    x += Math.cos(ang) * d; y += Math.sin(ang) * d
    pts.push({ x, y })
    // 太さは根元が太く先が尖る。途中で少し膨らむ所 (光の溜まり) を作る
    const u = s / steps
    w.push(rootW * (1 - u) * (0.75 + rnd(s, seed + 2) * 0.55))
  }
  return { pts, w }
}

// ---- 割れたガラスの欠片 ----

/** 面の割れた欠片を1枚 (明るい面と暗い面の2枚の三角)。 */
function drawShard(ctx: CanvasRenderingContext2D, x: number, y: number, size: number, rot: number, light: number, a: number, seed: number): void {
  if (a <= 0.01 || size <= 0.4) return
  const k = 3 + (rnd(seed, 71) < 0.5 ? 0 : 1)   // 三角か四角
  const pts: Pt[] = []
  for (let i = 0; i < k; i++) {
    const ang = rot + (i / k) * Math.PI * 2 + (rnd(seed * 7 + i, 72) - 0.5) * 1.1
    const r = size * (i === 0 ? 1.4 : 0.55 + rnd(seed * 7 + i, 73) * 0.5)   // 1か所だけ長く尖る
    pts.push({ x: x + Math.cos(ang) * r, y: y + Math.sin(ang) * r })
  }
  // 暗い面 (光を通さない側) は普通の塗りで、明るい面は常夜燈の光を映して足し算で
  ctx.save(); ctx.globalCompositeOperation = 'source-over'
  fillPoly(ctx, pts, rgba(LAMP.glassDark, a * 0.55))
  ctx.restore()
  const half: Pt[] = [pts[0], pts[1], { x, y }]
  fillPoly(ctx, half, rgba(LAMP.mid, a * (0.45 + 0.5 * light)))
  fillPoly(ctx, [pts[0], { x: (pts[0].x + x) / 2, y: (pts[0].y + y) / 2 }, pts[k - 1]], rgba(LAMP.core, a * 0.7 * light))
}

// ================= 普段の致命: ひびから常夜燈の光が漏れる =================

export const CRACK = {
  /** ひびが伸びきるまで (s)。 */
  spreadSec: 0.13,
  /** 光が最も強い一瞬 (当たり) から、揺らぎながら保つ間。 */
  holdSec: 0.62,
  /** そこから消えるまで。 */
  fadeSec: 0.42,
  /** 描く長さ (FX の寿命)。 */
  lifeSec: 1.15,
  /** 本の枝 (幹)。 */
  trunks: 7,
  /** 大きさ (10-20 作者「エフェクトもなんかちっちゃい」)。長さ・光のにじみ・太さを、前の版 (1・1・1) からこの倍に。
   *  太さは長さほど伸ばさない = 細長い線に寄らず、太い面のひびのまま広がる。 */
  lenMul: 1.75,
  bloomMul: 1.6,
  widthMul: 1.45,
  /** 当たりの止め (190ms) の間はゲームの時計が進まずひびが伸びない。止めの間にも開いて見えるよう、当たりの瞬間にこれだけ開いておく。 */
  revealAtHit: 0.45,
} as const

export interface CrackInput {
  /** 刺し傷 (ひびの起点)。 */
  x: number; y: number
  /** 突いた向き (主人公 → ボス)。 */
  dir: 1 | -1
  /** 当たりからの秒。 */
  ageSec: number
  /** ボスの大きさの倍率 (人の背丈 = 1)。 */
  scale: number
  /** 床の高さ (欠片が落ちて止まる所)。 */
  floorY: number
}

function crackLight(age: number): number {
  if (age < 0) return 0
  // 当たりの一瞬だけ強く、すぐ常夜燈くらいの明るさへ落ち着いて揺らぐ → 消える
  const flash = age < 0.09 ? 1 - age / 0.09 : 0
  const base = age < CRACK.holdSec ? 1 : 1 - clamp01((age - CRACK.holdSec) / CRACK.fadeSec)
  return clamp01((0.62 + 0.38 * flash) * base * base) * lampFlicker(age + 0.3)
}

/** 普段の致命のひび (世界の層・カメラの内側で描く)。 */
export function drawLampCrack(ctx: CanvasRenderingContext2D, c: CrackInput): void {
  const age = c.ageSec
  if (age < 0 || age > CRACK.lifeSec) return
  const S = c.scale
  const light = crackLight(age)
  const r0 = CRACK.revealAtHit
  const reveal = r0 + (1 - r0) * (1 - Math.pow(1 - clamp01(age / CRACK.spreadSec), 2))
  const L = CRACK.lenMul, B = CRACK.bloomMul, W = CRACK.widthMul
  ctx.save()
  ctx.globalCompositeOperation = 'lighter'
  // 刺し傷の奥の光 (いちばん明るい所)。外のにじみを広く、芯は白く飛ばさない程度に
  glow(ctx, c.x, c.y, 54 * B * S * (0.8 + 0.2 * light), LAMP.deep, 0.46 * light)
  glow(ctx, c.x, c.y, 26 * B * S, LAMP.mid, 0.3 * light)
  glow(ctx, c.x, c.y, 12 * S, LAMP.core, 0.55 * light)
  // ひび: 刺した向きの奥 (ボスの体の中) へ扇に広がる。上下にも少し回る
  for (let i = 0; i < CRACK.trunks; i++) {
    const spread = (i / (CRACK.trunks - 1) - 0.5) * 1.9 + (rnd(i, 11) - 0.5) * 0.3
    const ang = (c.dir > 0 ? 0 : Math.PI) + spread * c.dir
    const len = (28 + rnd(i, 12) * 26) * L * S
    const trunk = branch({ x: c.x, y: c.y }, ang, len, (4.6 + rnd(i, 13) * 1.8) * W * S, 100 + i * 17, 4)
    const tr = cutFissure(trunk, reveal)
    drawLeak(ctx, tr, light * (0.8 + 0.2 * rnd(i, 14)), 16 * B * S, 200 + i)
    // 枝分かれ (幹の途中から1本・幹が伸びきってから)
    const at = 1 + Math.floor(rnd(i, 15) * 2)
    if (reveal > 0.55 && trunk.pts[at]) {
      const side = rnd(i, 16) < 0.5 ? 1 : -1
      const sub = branch(trunk.pts[at], ang + side * (0.6 + rnd(i, 17) * 0.5), len * 0.45, trunk.w[at] * 0.8, 300 + i * 13, 2)
      drawLeak(ctx, cutFissure(sub, clamp01((reveal - 0.55) / 0.45)), light * 0.8, 11 * B * S, 400 + i)
    }
  }
  // こぼれるガラスの欠片 (刺し傷から下へ・床で止まって薄れる)
  for (let k = 0; k < 24; k++) {
    const born = 0.02 + rnd(k, 21) * 0.18
    const t = age - born
    if (t < 0) continue
    const life = 0.75 + rnd(k, 22) * 0.3
    if (t > life) continue
    const vx = (c.dir * (-0.2 + rnd(k, 23) * 1.1)) * 160 * S
    const vy = (-70 - rnd(k, 24) * 140) * S
    let x = c.x + vx * t, y = c.y + vy * t + 620 * t * t
    if (y > c.floorY - 2) { y = c.floorY - 2; x = c.x + vx * Math.min(t, 0.4) }
    const a = Math.pow(1 - t / life, 1.2)
    drawShard(ctx, x, y, (3.2 + rnd(k, 25) * 4.4) * S, t * (6 + rnd(k, 26) * 8) + k, light, a, k + 1)
  }
  // ひびから漏れた光が、ほどけて上へ昇る (常夜燈の側へ・本人へは戻らない)
  for (let m = 0; m < 10; m++) {
    const born = 0.18 + rnd(m, 31) * 0.4
    const t = age - born
    const life = 0.6
    if (t < 0 || t > life) continue
    const q = t / life
    const x = c.x + c.dir * (14 + rnd(m, 32) * 90) * S + Math.sin(t * 7 + m) * 4
    const y = c.y + (rnd(m, 33) - 0.5) * 90 * S - q * 90 * S
    // 1粒は常夜燈の光の正本 (src/lamp_mote.ts・10-31)。前の版 (にじみだけ・半径 7〜12×S) = DEV ?lampMote=old。
    if (lampMoteOld()) glow(ctx, x, y, (7 + rnd(m, 34) * 5) * S, LAMP.mid, 0.55 * Math.sin(Math.PI * q))
    else drawLampMote(ctx, x, y, 0.75 * Math.sin(Math.PI * q))
  }
  ctx.restore()
}

// ================= 止め: 割れたガラスの裂け目から常夜燈の光が漏れ、砕ける =================

export interface GlassSeamInput {
  /** 裂け目の中心 (中点)。 */
  cx: number; cy: number
  ang: number
  len: number
  /** 開いた割合 0〜1 と、どちらの端から開くか。 */
  reveal: number
  fromEnd: 1 | -1
  /** 光の強さ (揺らぎ込み)。 */
  light: number
  scale: number
  seed: number
  /** 開く頭の光を出すか。 */
  head: boolean
}

/** 止めの裂け目1本: ガラスの割れ目のように角ばって折れ、奥から常夜燈の光が漏れる。脇に割れた面 (欠片) が浮く。 */
export function drawGlassSeam(ctx: CanvasRenderingContext2D, s: GlassSeamInput): void {
  const S = s.scale
  const ux = Math.cos(s.ang), uy = Math.sin(s.ang)
  const half = s.len / 2
  const start = { x: s.cx - ux * half * s.fromEnd, y: s.cy - uy * half * s.fromEnd }
  // 端から端へ。中ほどが一番太い (光の溜まり)
  const N = 6
  const pts: Pt[] = [], w: number[] = []
  for (let i = 0; i <= N; i++) {
    const u = i / N
    const along = (u - 0.5) * s.len * s.fromEnd
    const kink = (i === 0 || i === N) ? 0 : (rnd(i, s.seed) - 0.5) * 22 * S   // 折れ目 (角ばる)
    pts.push({ x: s.cx + ux * along - uy * kink, y: s.cy + uy * along + ux * kink })
    w.push((6 + rnd(i, s.seed + 1) * 3.5) * S * Math.pow(Math.sin(Math.PI * u), 0.7))
  }
  void start
  const f = cutFissure({ pts, w }, s.reveal)
  drawLeak(ctx, f, s.light * 0.6, 30 * S, s.seed + 9)
  // 割れた面: 裂け目の脇にガラスの欠片が浮いて光を映す (手本 a の体に走る結晶の面)
  for (let k = 0; k < 4; k++) {
    const u = 0.15 + rnd(k, s.seed + 20) * 0.7
    const along = s.fromEnd > 0 ? u : 1 - u
    if (along > s.reveal) continue
    const side = rnd(k, s.seed + 21) < 0.5 ? 1 : -1
    const off = (u - 0.5) * s.len * s.fromEnd
    const px = s.cx + ux * off - uy * side * (10 + rnd(k, s.seed + 22) * 10) * S
    const py = s.cy + uy * off + ux * side * (10 + rnd(k, s.seed + 22) * 10) * S
    drawShard(ctx, px, py, (6 + rnd(k, s.seed + 23) * 6) * S, s.ang + side * 0.7, s.light * 0.8, 0.7, s.seed * 5 + k)
  }
  // 開く頭の光
  if (s.head && s.reveal < 1 && f.pts.length > 0) {
    const tip = f.pts[f.pts.length - 1]
    glow(ctx, tip.x, tip.y, 16 * S, LAMP.mid, 0.5)
  }
}

export interface GlassBurstInput {
  cx: number; cy: number
  /** 弾けてからの ms。 */
  ms: number
  scale: number
  floorY: number
}

/** 止めの弾け: 常夜燈の光が一度開き、割れたガラスの大きな欠片が上と外へ砕け散って、床に落ちて薄れる。 */
export function drawGlassBurst(ctx: CanvasRenderingContext2D, b: GlassBurstInput): void {
  const d = b.ms
  if (d < 0 || d > 2600) return
  const S = b.scale
  // 光が開く (白で画面を隠さない・常夜燈の青)
  if (d < 520) {
    const k = d / 520
    glow(ctx, b.cx, b.cy, (70 + 150 * (1 - Math.pow(1 - k, 3))) * S, LAMP.deep, 0.62 * Math.pow(1 - k, 1.4))
    glow(ctx, b.cx, b.cy, (30 + 50 * k) * S, LAMP.mid, 0.7 * Math.pow(1 - k, 1.8))
    glow(ctx, b.cx, b.cy, 22 * S, LAMP.core, 0.9 * Math.pow(1 - k, 2.4))
  }
  // 残る光 (欠片が降る間、常夜燈くらいの明るさで揺らいで消える)
  if (d >= 200 && d < 1800) {
    const q = (d - 200) / 1600
    glow(ctx, b.cx, b.cy + 10 * S, 60 * S, LAMP.deep, 0.22 * Math.sin(Math.PI * Math.min(1, q * 1.4)) * lampFlicker(d / 1000))
  }
  // 大きな欠片: 外と上へ飛び、回りながら重さで落ち、床で止まって薄れる
  const N = 46
  for (let i = 0; i < N; i++) {
    const big = i < 16
    const ang = -Math.PI / 2 + (rnd(i, 81) - 0.5) * Math.PI * 1.7
    const sp = (big ? 260 + rnd(i, 82) * 260 : 220 + rnd(i, 82) * 380) * S
    const t = d / 1000
    const drag = (1 - Math.exp(-1.8 * t)) / 1.8   // 空気で緩む
    let x = b.cx + Math.cos(ang) * sp * drag + (rnd(i, 83) - 0.5) * 30 * S
    let y = b.cy + Math.sin(ang) * sp * drag + 520 * t * t + (rnd(i, 84) - 0.5) * 40 * S
    let rot = rnd(i, 85) * 6 + t * (big ? 4 : 9) * (rnd(i, 86) < 0.5 ? 1 : -1)
    const landed = y > b.floorY - 3
    if (landed) { y = b.floorY - 3 - rnd(i, 87) * 4; rot = rnd(i, 88) * 6 }
    const size = (big ? 11 + rnd(i, 89) * 11 : 4 + rnd(i, 89) * 4) * S
    const life = 1600 + rnd(i, 90) * 900
    const a = d < 40 ? d / 40 : Math.pow(clamp01(1 - (d - 40) / life), landed ? 1.6 : 1)
    const light = (landed ? 0.35 : 0.85) * lampFlicker(t, i)
    if (landed) {
      ctx.save(); ctx.translate(x, y); ctx.scale(1, 0.45); ctx.translate(-x, -y)   // 床に寝る
      drawShard(ctx, x, y, size, rot, light, a, i + 11)
      ctx.restore()
    } else drawShard(ctx, x, y, size, rot, light, a, i + 11)
    void x
  }
  // 細かい粒は丸いにじみで (棒・点の列にしない)
  for (let m = 0; m < 10; m++) {
    const born = 120 + rnd(m, 91) * 500
    const t = d - born, life = 900
    if (t < 0 || t > life) continue
    const q = t / life
    const x = b.cx + (rnd(m, 92) - 0.5) * 160 * S + Math.sin(t / 200 + m) * 5
    const y = b.cy + (rnd(m, 93) - 0.5) * 80 * S - q * 90 * S
    glow(ctx, x, y, (6 + rnd(m, 94) * 5) * S, LAMP.mid, 0.5 * Math.sin(Math.PI * q))
  }
}
